/**
 * One Close create eligibility decision for planner, SQL selection, and write guards.
 * Configurable policy (deceased / equity min / obituary) is explicit; evidence still
 * has to be valid. Obituary is optional for Close (SO-17) unless the close gate
 * requires it and the obituary step is still on the workflow.
 */
import { OBITUARY_CONFIRMED_MATCH_STATUSES } from "./obituary-outcome.js";
import { workflowHasObituaryStep } from "./obituary-steps.js";
import {
  CLOSE_EQUITY_MIN_USD,
  closePushBlockedReason,
  finiteNumber,
  isDeceased,
  type CloseCreateDecision,
  type CloseCreateFailureClass,
  type ClosePushBlockedReason
} from "./property-equity.js";

export type CloseEligibilityPolicy = {
  deceasedRequired: boolean;
  equityMinUsd: number;
  obituaryRequired: boolean;
};

export const DEFAULT_CLOSE_ELIGIBILITY_POLICY: CloseEligibilityPolicy = {
  deceasedRequired: true,
  equityMinUsd: CLOSE_EQUITY_MIN_USD,
  obituaryRequired: false
};

export type CloseEligibilityFacts = {
  closeLeadId?: string | null;
  deceased?: unknown;
  estimatedEquity?: unknown;
  ownerRaw?: unknown;
  propertyValue?: unknown;
  plv?: unknown;
  obituaryMatchStatus?: unknown;
  obituaryUrl?: unknown;
};

export type CloseEligibilityDecision =
  | { action: "create"; failureClass: null }
  | { action: "patch"; failureClass: null }
  | { action: "refuse"; failureClass: CloseCreateFailureClass };

const CONFIRMED_OBIT = new Set<string>(OBITUARY_CONFIRMED_MATCH_STATUSES);

export const CLOSE_DECEASED_SQL =
  "(deceased in (1, '1') or lower(trim(coalesce(deceased, ''))) in ('yes', 'y', 'true', '1', 'deceased'))";

export const CLOSE_CONFIRMED_OBIT_SQL =
  "lower(trim(coalesce(obituary_match_status, ''))) in ('confirmed_match', 'operator_accepted')";

export const CLOSE_OBITUARY_URL_SQL =
  "(obituary_url is not null and trim(obituary_url) != '')";

export function closeEligibilityPolicyFromWorkflow(document: {
  steps: ReadonlyArray<{ tools: readonly string[]; gate?: Record<string, unknown> | undefined }>;
}): CloseEligibilityPolicy {
  const close = document.steps.find((step) => step.tools.includes("close_create"));
  const obituaryStepPresent = workflowHasObituaryStep(document);
  const gate = close?.gate ?? {};
  const equityMinUsd = finiteNumber(gate.equityMinUsd);
  return {
    deceasedRequired: gate.deceasedRequired !== false,
    equityMinUsd: equityMinUsd ?? CLOSE_EQUITY_MIN_USD,
    // Removing the obit step disables the adapter requirement even if the close gate still says required.
    obituaryRequired: gate.obituaryRequired === true && obituaryStepPresent
  };
}

export function isConfirmedObituary(value: unknown): boolean {
  return CONFIRMED_OBIT.has(String(value ?? "").replaceAll(/\s+/g, " ").trim().toLowerCase());
}

export function hasDeceasedProof(facts: CloseEligibilityFacts): boolean {
  return isDeceased(facts.deceased) || isConfirmedObituary(facts.obituaryMatchStatus);
}

export function hasObituaryEvidence(facts: CloseEligibilityFacts): boolean {
  if (isConfirmedObituary(facts.obituaryMatchStatus)) return true;
  return Boolean(String(facts.obituaryUrl ?? "").trim());
}

export function decideCloseEligibility(
  facts: CloseEligibilityFacts,
  policy: CloseEligibilityPolicy = DEFAULT_CLOSE_ELIGIBILITY_POLICY
): CloseEligibilityDecision {
  if (String(facts.closeLeadId ?? "").trim()) return { action: "patch", failureClass: null };
  if (policy.deceasedRequired && !hasDeceasedProof(facts)) {
    return { action: "refuse", failureClass: "close_not_deceased" };
  }
  const equity = finiteNumber(facts.estimatedEquity);
  if (equity == null) return { action: "refuse", failureClass: "close_equity_not_finite" };
  if (equity < policy.equityMinUsd) {
    return { action: "refuse", failureClass: "close_equity_below_threshold" };
  }
  if (policy.obituaryRequired && !hasObituaryEvidence(facts)) {
    return { action: "refuse", failureClass: "close_obituary_required" };
  }
  return { action: "create", failureClass: null };
}

export function canCreateCloseLead(
  input: CloseEligibilityFacts & { policy?: CloseEligibilityPolicy }
): boolean {
  return decideCloseEligibility(input, input.policy).action === "create";
}

export function decideCloseCreate(
  input: CloseEligibilityFacts & { policy?: CloseEligibilityPolicy }
): CloseCreateDecision {
  const decision = decideCloseEligibility(input, input.policy);
  if (decision.action !== "refuse") return { action: decision.action };
  return {
    action: "refuse",
    failureClass: decision.failureClass,
    reason: closeRefuseReason(decision.failureClass, input)
  };
}

export function closeCreateRefuseMessage(
  failureClass: CloseCreateFailureClass,
  policy: CloseEligibilityPolicy = DEFAULT_CLOSE_ELIGIBILITY_POLICY
): string {
  if (failureClass === "close_not_deceased") return "Close create requires a deceased owner.";
  if (failureClass === "close_equity_below_threshold") {
    return `Close create requires estimated equity of at least $${policy.equityMinUsd.toLocaleString("en-US")}.`;
  }
  if (failureClass === "close_obituary_required") return "Close create requires an obituary URL.";
  return "Close create requires finite estimated equity.";
}

export function closeCreateCandidateSql(policy: CloseEligibilityPolicy = DEFAULT_CLOSE_ELIGIBILITY_POLICY): {
  sql: string;
  binds: number[];
} {
  const clauses = ["(close_lead_id is null or trim(close_lead_id) = '')"];
  const binds: number[] = [];
  if (policy.deceasedRequired) {
    clauses.push(`(${CLOSE_DECEASED_SQL} or ${CLOSE_CONFIRMED_OBIT_SQL})`);
  }
  clauses.push("estimated_equity is not null");
  clauses.push("estimated_equity >= ?");
  binds.push(policy.equityMinUsd);
  if (policy.obituaryRequired) {
    clauses.push(`(${CLOSE_OBITUARY_URL_SQL} or ${CLOSE_CONFIRMED_OBIT_SQL})`);
  }
  return { sql: clauses.join("\n       and "), binds };
}

function closeRefuseReason(
  failureClass: CloseCreateFailureClass,
  input: CloseEligibilityFacts
): ClosePushBlockedReason {
  if (failureClass === "close_not_deceased") return "not_deceased";
  if (failureClass === "close_equity_below_threshold") return "blocked_equity_below_25k";
  if (failureClass === "close_obituary_required") return "blocked_no_obituary";
  const reason = closePushBlockedReason(input);
  return reason === "not_deceased" ? "blocked_no_equity" : reason;
}
